import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, Download, Construction } from '@mui/icons-material';
import { Button, Box, Typography, Card, Tabs, Tab, Paper, Chip } from '@mui/material';

import { AppHeader } from '@features/admin-v2';
import { DataMigrationUploadModal } from '@features/admin-v2';
import { DataMigrationSessionsList } from '@features/admin-v2';
import { useGetV2AdminOrganizationsByOrganizationIdQuery } from '@features/admin-v2/store/hooks';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`data-migration-tabpanel-${index}`}
      aria-labelledby={`data-migration-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function DataMigrationsListPage() {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [refreshSessions, setRefreshSessions] = useState(false);

  // Fetch organization data to display the name
  const { data: orgData } = useGetV2AdminOrganizationsByOrganizationIdQuery(
    { organizationId: organizationId! },
    { skip: !organizationId }
  );


  const handleUploadSuccess = useCallback(() => {
    // After successful upload, the modal should have returned a session ID
    // Close modal and refresh the sessions list
    setUploadModalOpen(false);
    setRefreshSessions(prev => !prev); // Toggle to trigger refresh
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!organizationId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Organization not found</h2>
            <Button onClick={() => navigate('/admin-v2/organizations')} className="mt-4">
              Back to Organizations
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Box mb={3}>
          <Button
            onClick={() => navigate(`/admin-v2/organization/${organizationId}`)}
            className="text-gray-600 hover:text-gray-900"
          >
            ← Back to Organization
          </Button>
        </Box>

        <Box mb={4}>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Patient Data Management</h1>
          {orgData?.name && (
            <Typography variant="subtitle1" className="text-gray-600 mb-3">
              {orgData.name}
            </Typography>
          )}

          <Paper sx={{ width: '100%', mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              aria-label="data migration tabs"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                label="Review & Import Data"
                icon={<Upload />}
                iconPosition="start"
                id="data-migration-tab-0"
                aria-controls="data-migration-tabpanel-0"
              />
              <Tab
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    Export Patients
                    <Chip
                      label="Coming Soon"
                      size="small"
                      color="default"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                }
                icon={<Download />}
                iconPosition="start"
                id="data-migration-tab-1"
                aria-controls="data-migration-tabpanel-1"
              />
            </Tabs>
          </Paper>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box display="flex" justifyContent="flex-end" mb={3}>
            <Button
              variant="contained"
              startIcon={<Upload />}
              onClick={() => setUploadModalOpen(true)}
            >
              Upload for Review
            </Button>
          </Box>

          <DataMigrationSessionsList
            organizationId={organizationId!}
            onRefresh={refreshSessions}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Card sx={{ textAlign: 'center', py: 8, px: 4 }}>
            <Box display="flex" justifyContent="center" mb={3}>
              <Construction sx={{ fontSize: 64, color: 'text.secondary' }} />
            </Box>

            <Typography variant="h5" gutterBottom fontWeight="medium">
              Export Feature Under Development
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 500, mx: 'auto', mt: 2 }}>
              We're working on the patient data export functionality. Soon you'll be able to export your organization's patient data in various formats for backup or migration purposes.
            </Typography>

            <Box sx={{ mt: 4, p: 3, bgcolor: 'info.lighter', borderRadius: 2, maxWidth: 600, mx: 'auto' }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Planned Features:
              </Typography>
              <Box component="ul" sx={{ textAlign: 'left', mt: 2, '& li': { mb: 1 } }}>
                <Typography component="li" variant="body2">
                  Export patient data in multiple formats (CSV, XML, JSON)
                </Typography>
                <Typography component="li" variant="body2">
                  Filter patients by date range, provider, or custom criteria
                </Typography>
                <Typography component="li" variant="body2">
                  Bulk export with progress tracking
                </Typography>
                <Typography component="li" variant="body2">
                  Secure, encrypted export files
                </Typography>
                <Typography component="li" variant="body2">
                  Export history and audit trail
                </Typography>
              </Box>
            </Box>

            <Chip
              label="Coming Soon"
              color="primary"
              variant="outlined"
              sx={{ mt: 3 }}
            />
          </Card>
        </TabPanel>
      </main>

      <DataMigrationUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={handleUploadSuccess}
        organizationId={organizationId}
      />
    </div>
  );
}